import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import { Search, Lock, MessageSquare, Send, ShieldCheck, Mail, Phone, Trophy, Check, CheckCheck, Clock3 } from 'lucide-react'

type DirectoryEntry = {
  id: string
  full_name: string
  headline: string | null
  bio: string | null
  avatar_url: string | null
  account_type: string | null
  outlet_name: string | null
  nationality: string | null
  email: string | null
  phone: string | null
  contact_visible_everyone: boolean
  is_connected: boolean
}

type ChatMessage = {
  id: string
  sender_type: 'org' | 'journalist'
  content: string
  created_at: string
  delivered_at: string | null
  read_at: string | null
}

function MessageTicks({ deliveredAt, readAt }: { deliveredAt: string | null; readAt: string | null }) {
  if (readAt) return <CheckCheck className="w-3.5 h-3.5 text-sky-300" />
  if (deliveredAt) return <CheckCheck className="w-3.5 h-3.5 text-white/60" />
  return <Check className="w-3.5 h-3.5 text-white/60" />
}

export function JournalistDirectoryView() {
  const { orgId } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [chatWith, setChatWith] = useState<DirectoryEntry | null>(null)

  const { data: journalists = [], isLoading } = useQuery({
    queryKey: ['journalist-directory'],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('journalist_directory' as any)
        .select('*')
        .order('full_name') as any)
      if (error) throw error
      return (data ?? []) as DirectoryEntry[]
    },
  })

  const { data: orgRankings = {} } = useQuery({
    queryKey: ['directory-org-rankings', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_org_rankings' as any)
        .select('journalist_id, org_rank, org_journalist_count')
        .eq('org_id', orgId!) as any)
      const map: Record<string, { org_rank: number; org_journalist_count: number }> = {}
      ;(data ?? []).forEach((r: any) => { map[r.journalist_id] = r })
      return map
    },
  })

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return journalists
    return journalists.filter(j =>
      j.full_name?.toLowerCase().includes(q) ||
      j.outlet_name?.toLowerCase().includes(q) ||
      j.headline?.toLowerCase().includes(q))
  }, [journalists, search])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#0D2240]">Journalist Directory</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Browse all registered journalists. Contact details are only shown for journalists you're accredited to, or who've chosen to make them visible to everyone. Otherwise, start a conversation.
        </p>
      </div>

      <div className="relative w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input placeholder="Search journalists..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-sm" />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">No journalists found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(j => {
            const contactVisible = j.is_connected || j.contact_visible_everyone
            const rank = (orgRankings as any)[j.id]
            return (
              <div key={j.id} className="border rounded-xl p-4 space-y-3 bg-white">
                <div className="flex items-center gap-2.5">
                  {j.avatar_url ? (
                    <img src={j.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#0D2240] flex items-center justify-center text-[#F0E4B8] text-xs font-bold shrink-0">
                      {(j.full_name ?? '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{j.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{j.outlet_name ?? j.headline ?? ''}</p>
                  </div>
                  {j.is_connected && (
                    <Badge variant="outline" className="ml-auto text-[10px] gap-1 shrink-0">
                      <ShieldCheck className="w-3 h-3" /> Connected
                    </Badge>
                  )}
                </div>

                {rank && (
                  <Badge variant="secondary" className="text-[10px] gap-1">
                    <Trophy className="w-3 h-3" /> #{rank.org_rank} of {rank.org_journalist_count} at your institution
                  </Badge>
                )}

                {contactVisible ? (
                  <div className="space-y-1 text-xs">
                    {j.email && <p className="flex items-center gap-1.5 text-muted-foreground"><Mail className="w-3 h-3" /> {j.email}</p>}
                    {j.phone && <p className="flex items-center gap-1.5 text-muted-foreground"><Phone className="w-3 h-3" /> {j.phone}</p>}
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
                    <Lock className="w-3 h-3" /> Contact details locked
                  </p>
                )}

                <Button size="sm" variant="outline" className="w-full gap-1.5 text-xs h-7" onClick={() => setChatWith(j)}>
                  <MessageSquare className="w-3.5 h-3.5" /> Message
                </Button>
              </div>
            )
          })}
        </div>
      )}

      <Sheet open={!!chatWith} onOpenChange={o => !o && setChatWith(null)}>
        <SheetContent side="right" className="w-[420px] flex flex-col">
          {chatWith && <ChatPanel orgId={orgId!} journalist={chatWith} onSent={() => qc.invalidateQueries({ queryKey: ['journalist-directory'] })} onError={(m) => toast({ title: 'Failed', description: m, variant: 'destructive' })} />}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function ChatPanel({ orgId, journalist, onError }: { orgId: string; journalist: DirectoryEntry; onSent: () => void; onError: (m: string) => void }) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')

  const { data: chat } = useQuery({
    queryKey: ['org-journalist-chat', orgId, journalist.id],
    queryFn: async () => {
      const { data: existing } = await (supabase
        .from('org_journalist_chats' as any)
        .select('id, status')
        .eq('org_id', orgId).eq('journalist_id', journalist.id)
        .maybeSingle() as any)
      if (existing) return existing
      const { data: created, error } = await (supabase
        .from('org_journalist_chats' as any)
        .insert({ org_id: orgId, journalist_id: journalist.id, initiated_by: 'org', status: 'pending' })
        .select('id, status').single() as any)
      if (error) throw error
      return created
    },
  })

  const { data: messages = [] } = useQuery({
    queryKey: ['org-journalist-chat-messages', chat?.id],
    enabled: !!chat?.id,
    refetchInterval: 5000,
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_journalist_chat_messages' as any)
        .select('id, sender_type, content, created_at, delivered_at, read_at')
        .eq('chat_id', chat!.id)
        .order('created_at') as any)
      return (data ?? []) as ChatMessage[]
    },
  })

  useEffect(() => {
    if (!chat?.id) return
    supabase.from('org_journalist_chat_messages' as any)
      .update({ read_at: new Date().toISOString() })
      .eq('chat_id', chat.id).eq('sender_type', 'journalist').is('read_at', null)
      .then(() => qc.invalidateQueries({ queryKey: ['org-journalist-chat-messages', chat.id] }))
  }, [chat?.id, messages.length])

  const send = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!chat?.id || !user) return
      const { error } = await (supabase
        .from('org_journalist_chat_messages' as any)
        .insert({ chat_id: chat.id, sender_type: 'org', sender_id: user.id, content: draft.trim() }) as any)
      if (error) throw error
    },
    onSuccess: () => { setDraft(''); qc.invalidateQueries({ queryKey: ['org-journalist-chat-messages', chat?.id] }) },
    onError: (e: any) => onError(e.message),
  })

  return (
    <>
      <SheetHeader className="mb-4">
        <SheetTitle>Chat with {journalist.full_name}</SheetTitle>
      </SheetHeader>
      {chat?.status === 'pending' && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2 mb-3">
          <Clock3 className="w-3.5 h-3.5 shrink-0" /> Message request sent — {journalist.full_name} hasn't accepted yet.
        </div>
      )}
      {chat?.status === 'declined' && (
        <div className="flex items-center gap-1.5 text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2 mb-3">
          This journalist declined your message request.
        </div>
      )}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No messages yet. Say hello.</p>}
        {messages.map(m => (
          <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.sender_type === 'org' ? 'ml-auto bg-[#0D2240] text-white' : 'bg-muted'}`}>
            <div>{m.content}</div>
            {m.sender_type === 'org' && (
              <div className="flex justify-end mt-0.5">
                <MessageTicks deliveredAt={m.delivered_at} readAt={m.read_at} />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-3 border-t">
        <Textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder="Write a message..." className="min-h-9 resize-none text-sm" rows={1} />
        <Button size="icon" disabled={!draft.trim() || send.isPending} onClick={() => send.mutate()}>
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </>
  )
}
